import { useEffect, useMemo, useState } from "react";
import { Sparkles, Upload, Loader2, RefreshCw, FileText, Cpu, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EXAM_LABELS, SUBJECT_LABELS, DIFFICULTY_LABELS } from "@/lib/pyqMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const EXAMS = ["JEE_MAIN", "JEE_ADVANCED", "NEET"];
const SUBJECTS = ["PHYSICS", "CHEMISTRY", "MATHEMATICS", "BOTANY", "ZOOLOGY"];

const StatusPill = ({ s }) => {
  const map = {
    PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    RUNNING: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    DONE: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    ERROR: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };
  return <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", map[s])}>{s}</span>;
};

const JobsList = ({ jobs, onRefresh, loading, onOpen }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <div className="mb-3 flex items-center justify-between">
      <span className="font-heading text-base font-semibold">Recent jobs</span>
      <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="refresh-jobs-btn" className="gap-1.5 text-xs">
        <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Refresh
      </Button>
    </div>
    {jobs.length === 0 ? (
      <p className="py-6 text-center text-sm text-muted-foreground">No jobs yet.</p>
    ) : (
      <div className="space-y-2">
        {jobs.map((j) => {
          const openable = j.status === "DONE" && (j.producedCount || 0) > 0 && onOpen;
          return (
            <button
              key={j.id}
              type="button"
              disabled={!openable}
              onClick={() => openable && onOpen(j.id)}
              data-testid={`job-${j.id}`}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left text-sm transition-colors",
                openable ? "cursor-pointer hover:border-primary/50 hover:bg-secondary/50" : "cursor-default"
              )}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary">
                {j.type === "VIGS" ? <Cpu className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{j.label}</div>
                <div className="text-xs text-muted-foreground">
                  {j.type} · produced {j.producedCount || 0}{j.error ? ` · ${j.error}` : ""}
                  {openable ? " · click to review →" : ""}
                </div>
              </div>
              <StatusPill s={j.status} />
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export const AdminGenerate = ({ onOpenJob }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ examType: "JEE_MAIN", subject: "PHYSICS", chapter: "", difficulty: "MEDIUM", questionType: "MCQ_SINGLE", count: 3 });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const chapters = useMemo(() => (meta?.chaptersBySubject?.[form.subject] || []), [meta, form.subject]);

  const loadJobs = () => {
    setLoading(true);
    api.get("/admin/jobs").then((r) => setJobs(r.data.filter((j) => j.type === "VIGS"))).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/pyq/meta").then((r) => setMeta(r.data)); loadJobs(); }, []);
  useEffect(() => {
    const anyRunning = jobs.some((j) => j.status === "RUNNING" || j.status === "PENDING");
    if (!anyRunning) return;
    const t = setInterval(loadJobs, 5000);
    return () => clearInterval(t);
  }, [jobs]);

  const generate = async () => {
    setSubmitting(true);
    try {
      await api.post("/admin/vigs/generate", { ...form, count: Number(form.count) });
      toast.success("Generation started — questions will appear in the review queue.");
      loadJobs();
    } catch {
      toast.error("Failed to start generation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="vigs-form">
        <div className="mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-semibold">VIGS Generator</h2>
        </div>
        <p className="mb-5 text-sm text-muted-foreground">Generate brand-new questions grounded in your PYQ corpus. They land in the review queue for approval.</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Exam</Label>
            <Select value={form.examType} onValueChange={(v) => setForm((f) => ({ ...f, examType: v }))}>
              <SelectTrigger data-testid="vigs-exam" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{EXAMS.map((e) => <SelectItem key={e} value={e}>{EXAM_LABELS[e]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v, chapter: "" }))}>
              <SelectTrigger data-testid="vigs-subject" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{SUBJECT_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Chapter</Label>
            <Select value={form.chapter || "any"} onValueChange={(v) => setForm((f) => ({ ...f, chapter: v === "any" ? "" : v }))}>
              <SelectTrigger data-testid="vigs-chapter" className="rounded-xl"><SelectValue placeholder="Any chapter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any chapter</SelectItem>
                {chapters.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Difficulty</Label>
            <Select value={form.difficulty} onValueChange={(v) => setForm((f) => ({ ...f, difficulty: v }))}>
              <SelectTrigger data-testid="vigs-difficulty" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{["EASY", "MEDIUM", "HARD"].map((d) => <SelectItem key={d} value={d}>{DIFFICULTY_LABELS[d]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>How many</Label>
            <Input type="number" min={1} max={8} data-testid="vigs-count" value={form.count} onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))} className="rounded-xl" />
          </div>
        </div>
        <Button onClick={generate} disabled={submitting} data-testid="vigs-generate-btn" className="mt-5 w-full rounded-xl py-6">
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generate questions
        </Button>
      </div>
      <JobsList jobs={jobs} onRefresh={loadJobs} loading={loading} onOpen={onOpenJob} />
    </div>
  );
};

export const AdminUpload = ({ onOpenJob }) => {
  const [meta, setMeta] = useState({ examType: "JEE_MAIN", subject: "PHYSICS", year: "2024", month: "", shift: "", date: "" });
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadJobs = () => {
    setLoading(true);
    api.get("/admin/jobs").then((r) => setJobs(r.data.filter((j) => j.type === "PDF"))).finally(() => setLoading(false));
  };
  useEffect(loadJobs, []);
  useEffect(() => {
    const anyRunning = jobs.some((j) => j.status === "RUNNING" || j.status === "PENDING");
    if (!anyRunning) return;
    const t = setInterval(loadJobs, 5000);
    return () => clearInterval(t);
  }, [jobs]);

  const addFiles = (list) => {
    const pdfs = Array.from(list).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) { toast.warning("Only PDF files are supported"); return; }
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...pdfs.filter((f) => !names.has(f.name + f.size))];
    });
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, j) => j !== i));
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  const upload = async () => {
    if (files.length === 0) { toast.warning("Choose at least one PDF first"); return; }
    setSubmitting(true);
    let ok = 0, failed = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress({ current: i + 1, total: files.length, name: files[i].name });
      const fd = new FormData();
      fd.append("file", files[i]);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      try {
        await api.post("/admin/pdf/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
        loadJobs();
      } catch (e) {
        failed += 1;
        toast.error(`${files[i].name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    if (ok) toast.success(`${ok} PDF${ok > 1 ? "s" : ""} queued — watch extraction progress on the right.`);
    setFiles([]);
    setProgress(null);
    setSubmitting(false);
    loadJobs();
    if (failed === 0) return;
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="pdf-form">
        <div className="mb-4 flex items-center gap-2">
          <Upload className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Ingest past-paper PDFs</h2>
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          data-testid="pdf-dropzone"
          className={cn("mb-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition-colors", drag ? "border-primary bg-primary/5" : "border-border")}
        >
          <Upload className="mb-2 h-7 w-7 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drag & drop one or more PDFs, or</p>
          <label className="mt-2 cursor-pointer text-sm font-medium text-primary hover:underline">
            browse files
            <input type="file" accept="application/pdf" multiple data-testid="pdf-file-input" className="hidden" onChange={(e) => { addFiles(e.target.files || []); e.target.value = ""; }} />
          </label>
        </div>

        {files.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="pdf-selected-list">
            {files.map((f, i) => (
              <div key={f.name + f.size} data-testid={`pdf-selected-${i}`} className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                {!submitting && (
                  <button type="button" onClick={() => removeFile(i)} data-testid={`pdf-remove-${i}`} className="shrink-0 text-muted-foreground hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Exam</Label>
            <Select value={meta.examType} onValueChange={(v) => setMeta((m) => ({ ...m, examType: v }))}>
              <SelectTrigger data-testid="pdf-exam" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{EXAMS.map((e) => <SelectItem key={e} value={e}>{EXAM_LABELS[e]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Select value={meta.subject} onValueChange={(v) => setMeta((m) => ({ ...m, subject: v }))}>
              <SelectTrigger data-testid="pdf-subject" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{SUBJECT_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Year</Label>
            <Input data-testid="pdf-year" value={meta.year} onChange={(e) => setMeta((m) => ({ ...m, year: e.target.value }))} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Shift</Label>
            <Input placeholder="Morning" data-testid="pdf-shift" value={meta.shift} onChange={(e) => setMeta((m) => ({ ...m, shift: e.target.value }))} className="rounded-xl" />
          </div>
        </div>
        <Button onClick={upload} disabled={submitting} data-testid="pdf-upload-btn" className="mt-5 w-full rounded-xl py-6">
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {submitting && progress
            ? `Uploading ${progress.current} of ${progress.total}…`
            : files.length > 1 ? `Upload & extract ${files.length} PDFs` : "Upload & extract"}
        </Button>
        <p className="mt-3 text-xs text-muted-foreground">The same exam/subject metadata applies to all selected PDFs. Each becomes its own job on the right. Text-based PDFs work best; printed solutions are preserved, handwritten ones get an AI-written solution.</p>
      </div>
      <JobsList jobs={jobs} onRefresh={loadJobs} loading={loading} onOpen={onOpenJob} />
    </div>
  );
};

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/users").then((r) => setUsers(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/admin/users/${uid}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.user_id === uid ? { ...u, role } : u)));
      toast.success("Role updated");
    } catch {
      toast.error("Could not update role");
    }
  };

  const changeStatus = async (uid, status) => {
    try {
      await api.put(`/admin/users/${uid}/status`, { status });
      setUsers((prev) => prev.map((u) => (u.user_id === uid ? { ...u, status } : u)));
      toast.success(status === "ACTIVE" ? "Account activated" : "Account deactivated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-users">
      <h2 className="mb-4 font-heading text-lg font-semibold">Users ({users.length})</h2>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="space-y-2">
          {users.map((u) => {
            const status = u.status || "ACTIVE";
            const isAdmin = u.role === "ADMIN";
            return (
            <div key={u.user_id} data-testid={`user-${u.user_id}`} className={cn("flex items-center gap-3 rounded-xl border p-3", status === "DEACTIVATED" ? "border-red-200 bg-red-50/40 dark:bg-red-950/10" : "border-border")}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-bold uppercase">{(u.name || u.email)[0]}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{u.name || "—"}</div>
                <div className="truncate text-xs text-muted-foreground">{u.email} {u.examPreference ? `· ${u.examPreference}` : ""}</div>
              </div>
              <Badge variant="outline" className={cn("rounded-full", status === "DEACTIVATED" ? "border-red-300 text-red-600" : "border-emerald-300 text-emerald-600")} data-testid={`status-badge-${u.user_id}`}>
                {status === "DEACTIVATED" ? "Deactivated" : "Active"}
              </Badge>
              <Badge variant="outline" className="rounded-full">{u.authProvider === "google" ? "Google" : "Email"}</Badge>
              <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-28 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["STUDENT", "EXPERT", "ADMIN"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {!isAdmin && (
                status === "DEACTIVATED" ? (
                  <Button size="sm" variant="outline" data-testid={`activate-${u.user_id}`} onClick={() => changeStatus(u.user_id, "ACTIVE")} className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50">Activate</Button>
                ) : (
                  <Button size="sm" variant="outline" data-testid={`deactivate-${u.user_id}`} onClick={() => changeStatus(u.user_id, "DEACTIVATED")} className="rounded-xl border-red-300 text-red-600 hover:bg-red-50">Deactivate</Button>
                )
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
};
